import { useState, useRef, useEffect } from "react";
import { collection, doc, setDoc, serverTimestamp } from "firebase/firestore";
import { db } from "../firebase";
import './EcoChatbotWidget.css';

const CHATS_HISTORY_COLLECTION = "chats_history";

// ─── AI API CONFIGURATION ───────────────────────────────────────────────────────────
// You can switch between "openai", "groq", and "gemini" by changing the API_PROVIDER
const API_PROVIDER = process.env.REACT_APP_AI_PROVIDER || "openai"; // Options: "openai", "groq", or "gemini"

// API Keys (from environment variables)
const OPENAI_API_KEY = process.env.REACT_APP_OPENAI_API_KEY || "";
const GROQ_API_KEY = process.env.REACT_APP_GROQ_API_KEY || "";
const GEMINI_API_KEY = process.env.REACT_APP_GEMINI_API_KEY || "";

// Helper: true if current provider has key from .env (admin configures in .env only)
function hasConfiguredKey() {
  const provider = API_PROVIDER;
  if (provider === "openai") return (OPENAI_API_KEY || process.env.REACT_APP_OPENAI_API_KEY || "").trim().length > 0;
  if (provider === "groq") return (GROQ_API_KEY || process.env.REACT_APP_GROQ_API_KEY || "").trim().length > 0;
  if (provider === "gemini") return (GEMINI_API_KEY || process.env.REACT_APP_GEMINI_API_KEY || "").trim().length > 0;
  return false;
}
const CHAT_UNAVAILABLE_MSG = "Chat is currently unavailable. Please try again later.";

// ─── LANGUAGES ───────────────────────────────────────────────────────────────
const LANGUAGES = [
  { code: "en", label: "English", flag: "", name: "English" },
  { code: "te", label: "తెలుగు", flag: "", name: "Telugu" },
  { code: "hi", label: "हिन्दी", flag: "", name: "Hindi" },
  { code: "ta", label: "தமிழ்", flag: "", name: "Tamil" },
  { code: "kn", label: "ಕನ್ನಡ", flag: "", name: "Kannada" },
  { code: "ml", label: "മലയാളം", flag: "", name: "Malayalam" },
  { code: "mr", label: "मराठी", flag: "", name: "Marathi" },
  { code: "bn", label: "বাংলা", flag: "", name: "Bengali" },
  { code: "or", label: "ଓଡ଼ିଆ", flag: "", name: "Odia" },
  { code: "ur", label: "اردو", flag: "", name: "Urdu" },
];

const WELCOME = {
  en: "Hello! I'm Eco, your Ecohomely assistant!\n\nI can help you book home services, answer questions, or connect you with our team in Visakhapatnam\n\nHow can I help you today?",
  te: "నమస్కారం! నేను Eco, మీ Ecohomely assistant!\n\nVisakhapatnam లో home services కోసం help చేయడానికి నేను ఇక్కడ ఉన్నాను\n\nఈరోజు నేను మీకు ఎలా help చేయగలను?",
  hi: "नमस्ते! मैं Eco हूँ, आपका Ecohomely सहायक!\n\nVisakhapatnam में home services बुक करने या सवाल पूछने के लिए मैं यहाँ हूँ\n\nआज मैं आपकी कैसे मदद करूँ?",
  ta: "வணக்கம்! நான் Eco, உங்கள் Ecohomely உதவியாளர்!\n\nVisakhapatnam-ல் home services க்கு உதவ நான் இங்கே இருக்கிறேன்\n\nஇன்று நான் உங்களுக்கு எப்படி உதவலாம்?",
  kn: "ನಮಸ್ಕಾರ! ನಾನು Eco, ನಿಮ್ಮ Ecohomely ಸಹಾಯಕ!\n\nVisakhapatnam ನಲ್ಲಿ home services ಗಾಗಿ ಸಹಾಯ ಮಾಡಲು ನಾನು ಇಲ್ಲಿದ್ದೇನೆ\n\nಇಂದು ನಾನು ನಿಮಗೆ ಹೇಗೆ ಸಹಾಯ ಮಾಡಲಿ?",
  ml: "നമസ്കാരം! ഞാൻ Eco, നിങ്ങളുടെ Ecohomely അസിസ്റ്റന്റ്!\n\nVisakhapatnam-ൽ home services-നായി ഞാൻ ഇവിടെ ഉണ്ട്\n\nഇന്ന് ഞാൻ നിങ്ങളെ എങ്ങനെ സഹായിക്കണം?",
  mr: "नमस्कार! मी Eco, तुमचा Ecohomely सहाय्यक!\n\nVisakhapatnam मध्ये home services साठी मी इथे आहे\n\nआज मी तुम्हाला कशी मदत करू?",
  bn: "নমস্কার! আমি Eco, আপনার Ecohomely সহকারী!\n\nVisakhapatnam-এ home services-এর জন্য আমি এখানে আছি\n\nআজ আমি আপনাকে কীভাবে সাহায্য করতে পারি?",
  or: "ନମସ୍କାର! ମୁଁ Eco, ଆପଣଙ୍କ Ecohomely ସହାୟକ!\n\nVisakhapatnam ରେ home services ପାଇଁ ମୁଁ ଏଠାରେ ଅଛି\n\nଆଜି ମୁଁ ଆପଣଙ୍କୁ କିପରି ସାହାଯ୍ୟ କରିବି?",
  ur: "السلام علیکم! میں Eco ہوں، آپ کا Ecohomely مددگار!\n\nVisakhapatnam میں home services کے لیے میں یہاں ہوں\n\nآج میں آپ کی کیسے مدد کروں?",
};

// ─── QUICK REPLIES ───────────────────────────────────────────────────────────
const QUICK_REPLIES = {
  en: [
    { label: "About Ecohomely", text: "Tell me about Ecohomely" },
    { label: "Book a Service", text: "I'd like to request a service" },
    { label: "How it Works", text: "How does Ecohomely work?" },
    { label: "Join as Worker", text: "I am a worker, how do I join?" },
    { label: "Talk to Team", text: "I want to speak to the team" },
  ],
  te: [
    { label: "మా గురించి", text: "Ecohomely గురించి చెప్పండి" },
    { label: "Service కావాలి", text: "నాకు service కావాలి" },
    { label: "ఎలా పని చేస్తుంది", text: "Ecohomely ఎలా పని చేస్తుంది?" },
    { label: "Worker గా చేరు", text: "నేను worker ని, ఎలా join అవ్వాలి?" },
    { label: "Team తో మాట్లాడు", text: "Team తో మాట్లాడాలి" },
  ],
  hi: [
    { label: "हमारे बारे में", text: "Ecohomely के बारे में बताइए" },
    { label: "Service चाहिए", text: "मुझे service चाहिए" },
    { label: "कैसे काम करता है", text: "Ecohomely कैसे काम करता है?" },
    { label: "Worker बनें", text: "मैं worker हूँ, कैसे जुड़ूँ?" },
    { label: "Team से बात करें", text: "Team से बात करनी है" },
  ],
  ta: [
    { label: "எங்களைப் பற்றி", text: "Ecohomely பற்றி சொல்லுங்கள்" },
    { label: "Service வேண்டும்", text: "எனக்கு service வேண்டும்" },
    { label: "எப்படி வேலை செய்யும்", text: "Ecohomely எப்படி வேலை செய்யும்?" },
    { label: "Worker ஆக சேர", text: "நான் worker, எப்படி சேர்வது?" },
    { label: "Team பேசு", text: "Team உடன் பேச வேண்டும்" },
  ],
  kn: [
    { label: "ನಮ್ಮ ಬಗ್ಗೆ", text: "Ecohomely ಬಗ್ಗೆ ಹೇಳಿ" },
    { label: "Service ಬೇಕು", text: "ನನಗೆ service ಬೇಕು" },
    { label: "ಹೇಗೆ ಕೆಲಸ ಮಾಡುತ್ತದೆ", text: "Ecohomely ಹೇಗೆ ಕೆಲಸ ಮಾಡುತ್ತದೆ?" },
    { label: "Worker ಆಗಿ ಸೇರಿ", text: "ನಾನು worker, ಹೇಗೆ ಸೇರಲಿ?" },
    { label: "Team ಮಾತಾಡಿ", text: "Team ಜೊತೆ ಮಾತಾಡಬೇಕು" },
  ],
  ml: [
    { label: "ഞങ്ങളെ പറ്റി", text: "Ecohomely പറ്റി പറയൂ" },
    { label: "Service வேணും", text: "എനിക്ക് service വേണം" },
    { label: "എങ്ങനെ പ്രവർത്തിക്കുന്നു", text: "Ecohomely എങ്ങനെ പ്രവർത്തിക്കുന്നു?" },
    { label: "Worker ആയി ചേരൂ", text: "ഞാൻ worker ആണ്, എങ്ങനെ ചേരാം?" },
    { label: "Team സംസാരിക്കൂ", text: "Team-മായി സംസാരിക്കണം" },
  ],
  mr: [
    { label: "आमच्याबद्दल", text: "Ecohomely बद्दल सांगा" },
    { label: "Service हवी", text: "मला service हवी आहे" },
    { label: "कसे काम करते", text: "Ecohomely कसे काम करते?" },
    { label: "Worker म्हणून जोडा", text: "मी worker आहे, कसे जोडावे?" },
    { label: "Team शी बोला", text: "Team शी बोलायचे आहे" },
  ],
  bn: [
    { label: "আমাদের সম্পর্কে", text: "Ecohomely সম্পর্কে বলুন" },
    { label: "Service দরকার", text: "আমার service দরকার" },
    { label: "কীভাবে কাজ করে", text: "Ecohomely কীভাবে কাজ করে?" },
    { label: "Worker হিসেবে যোগ দিন", text: "আমি worker, কীভাবে যোগ দেব?" },
    { label: "Team এর সাথে কথা", text: "Team এর সাথে কথা বলতে চাই" },
  ],
  or: [
    { label: "ଆମ ବିଷୟରେ", text: "Ecohomely ବିଷୟରେ କୁହନ୍ତୁ" },
    { label: "Service ଦରକାର", text: "ମୋର service ଦରକାର" },
    { label: "କିପରି କାମ କରେ", text: "Ecohomely କିପରି କାମ କରେ?" },
    { label: "Worker ଭାବେ ଯୋଗ ଦିଅ", text: "ମୁଁ worker, କିପରି ଯୋଗ ଦେବି?" },
    { label: "Team ସହ କଥା", text: "Team ସହ କଥା ହେବାକୁ ଚାହୁଁଛି" },
  ],
  ur: [
    { label: "ہمارے بارے میں", text: "Ecohomely کے بارے میں بتائیں" },
    { label: "Service چاہیے", text: "مجھے service چاہیے" },
    { label: "کیسے کام کرتا ہے", text: "Ecohomely کیسے کام کرتا ہے؟" },
    { label: "Worker بنیں", text: "میں worker ہوں، کیسے جوائن کروں؟" },
    { label: "Team سے بات کریں", text: "Team سے بات کرنی ہے" },
  ],
};

// ─── PLACEHOLDERS ────────────────────────────────────────────────────────────
const PLACEHOLDERS = {
  en: "Type your message...", te: "మీ message టైప్ చేయండి...",
  hi: "अपना message टाइप करें...", ta: "உங்கள் message தட்டச்சு செய்யுங்கள்...",
  kn: "ನಿಮ್ಮ message ಟೈಪ್ ಮಾಡಿ...", ml: "നിങ്ങളുടെ message ടൈപ്പ് ചെയ്യൂ...",
  mr: "तुमचा message टाइप करा...", bn: "আপনার message টাইপ করুন...",
  or: "ଆପଣଙ୍କ message ଟାଇପ୍ କରନ୍ତୁ...", ur: "اپنا پیغام لکھیں...",
};

// ─── SYSTEM PROMPT ────────────────────────────────────────────────────────────
const buildPrompt = (langCode, langName) => `You are Eco, the AI assistant for Ecohomely — Visakhapatnam-based worker-first, zero-commission home services platform.

══════════════════════════════════════
STEP 1 — IDENTIFY: CUSTOMER OR WORKER?
══════════════════════════════════════
At the START of every conversation, figure out if the person is a CUSTOMER or WORKER before answering anything detailed.

CUSTOMER signals: wants to hire/book a service, find a worker, home repair, "need plumber/cleaning/AC repair", "how do I book"
WORKER signals: "I am a plumber/electrician", "how do I join", "commission untunda", "nenu worker ni", "bookings vasthaya", "registration"
AMBIGUOUS (e.g. "how does it work?", "what is Ecohomely?"): Ask ONE question first:
  → "Are you looking to hire a service, or are you a service professional wanting to join Ecohomely?"

Once identity is known, remember it for the ENTIRE conversation.

══════════════════════════════════════
STEP 2 — ANSWER FROM THEIR PERSPECTIVE
══════════════════════════════════════
Always respond from the correct perspective:

SAME QUESTION — TWO DIFFERENT ANSWERS:

"How does Ecohomely work?"
→ IF CUSTOMER: "Ecohomely is simple!
   1. Open app → select service (plumber, electrician, etc.)
   2. See verified nearby workers with ratings
   3. Call them directly & discuss job
   4. Work done → pay worker directly (Cash/UPI)
   Zero charges to you from Ecohomely!"

→ IF WORKER: "Joining Ecohomely is easy!
   1. Register free on the app
   2. Complete profile → go Online
   3. Nearby customers see your profile & call you
   4. Do the job → customer pays you directly
   0% commission — full earnings are yours!"

"What is Ecohomely?"
→ IF CUSTOMER: A platform to easily find verified trusted local workers near you — zero middlemen, zero platform fee.
→ IF WORKER: A platform to get more customers in your area — zero commission, full earnings, free to join.

Apply this logic to EVERY question. Same info, different angle — customer side or worker side.

══════════════════════════════════════
STEP 3 — IGNORE OFF-TOPIC QUESTIONS
══════════════════════════════════════
If the user asks ANYTHING not related to Ecohomely (cricket, weather, cooking, politics, movies, jokes, other companies, general knowledge, etc.):
Reply ONLY with: "I can only help with Ecohomely-related questions."
Do NOT explain, engage, or expand. One line. Done.

══════════════════════════════════════
LANGUAGE RULE
══════════════════════════════════════
User selected "${langName}". Reply ONLY in ${langName} every time.
- Telugu: Warm Tenglish. E.g. "0% commission. Mee earnings full ga meeke vastayi" or use context-appropriate greeting
- Hindi: Simple conversational. E.g. "जी, 0% commission है। पैसे सीधे worker को जाते हैं।" (use "जी" which is gender-neutral)
- Tamil, Kannada, Malayalam, Marathi, Bengali, Odia: Natural and warm in that language. Use gender-neutral or context-appropriate greetings.
- Urdu: RTL, respectful. Use gender-neutral respectful terms.
- English: Simple, clear. Use "you" or context-appropriate greetings. Avoid assuming gender.

GENDER-NEUTRAL RULE (CRITICAL): Do NOT assume gender. The user could be male, female, or any gender.
- PREFERRED: Use "you" instead of "Sir" or "Madam" when possible
- If a greeting is absolutely necessary, use "Sir/Madam" together, or use language-specific neutral terms:
  * Hindi: "जी" (gender-neutral respectful term)
  * Telugu: Context-based or skip greeting
  * English: Skip greeting or use "you"
- NEVER default to "Sir" alone - it assumes male gender
- When in doubt, skip the greeting entirely and start with the message content
- Examples: "Ecohomely is simple!" (not "Sir, Ecohomely is simple!")
Keep under 120 words unless booking.

══════════════════════════════════════
COMPANY INFO
══════════════════════════════════════
- Ecohomely Enterprises | Launched Oct 2025
- Founder: Satya
- Visakhapatnam (Vizag), AP | Office: Madhurawada–Rushikonda IT SEZ, Hill No.3 (staff only)
- DPIIT: DIPP213164
- Phone: 9491499797 | Email: support@ecohomely.com | App: Play Store & App Store
- For any assistance or payment-related issues: Toll-free 9491499797 | support@ecohomely.com
- Tagline: "Reliable Services + Affordable Home Products"
- Traction: 1,200+ workers | 2,500+ customers | 100+ bookings | Zero complaints
- Working days: Monday to Friday, 9AM–6PM IST. Leave your message anytime; we'll be in touch during these hours.

SERVICES (ALL AVAILABLE - DO NOT SAY "WE DON'T OFFER" FOR ANY OF THESE):
Two Wheeler Mechanic, Four Wheeler Mechanic, AC Repair, Appliance Repair, Aquarium, Beauty & Wellness, Car Wash, Carpenter, Catering Services, Ceiling, Commercial Vehicle Mechanic, Driver, Electrician, Interior Designer, Laundry, Mehndi Artist Service, Men's Salon, Multi Vehicle Mechanic, Painter, Pest Control, Photography, Plumber, RO Water Purifier Service, Road Assistance, Shifting, Smart Home, Tutors, TC Repair, TV Repair, Water Can Delivery.

IMPORTANT: If a customer asks about ANY service, confirm it's available and guide them to book via the app. NEVER say "we don't offer this service" or "this service is not available" - ALL listed services are available.

══════════════════════════════════════
CUSTOMER Q&A
══════════════════════════════════════
- Platform charges? → 100% FREE for customers. Zero platform fees.
- Payment? → Direct to worker after service. Cash/UPI. Ecohomely doesn't handle payments.
- Advance? → Not needed. Pay after work.
- Fixed price? → No. Discuss with worker directly.
- Workers verified? → Yes, document-checked before listing.
- Ecohomely sends worker? → NO. Customer picks from profiles.
- Worker late? → Call worker. Repeated = support acts.
- Worker cancelled? → Repeated = account removal.
- Complaint? → 9491499797. Team acts.
- No smartphone? → Call 9491499797.
- Same worker again? → Open profile, call directly.
- Emergency/night? → Check online workers in app.
- Data safe? → Yes, never misused.
- Travel charges? → Worker may ask if far. Your decision.
- Insurance? → No. Ecohomely is platform only.
- Working hours / when are you available? → We're here Monday to Friday, 9AM–6PM IST. Leave a message anytime and we'll get back to you during these hours.

══════════════════════════════════════
WORKER Q&A
══════════════════════════════════════
- Joining fee? → ₹199 one-time joining fee for verification & platform access.
- Commission? → 0%. 100% earnings to worker. Zero commission.
- Monthly charges? → No mandatory monthly charges. Optional subscription plans coming soon.
- Subscription plans? → Future plans (Free/Silver/Gold) will have optional add-ons like higher visibility, priority listing, etc. Always free to use basic plan.
- Payment handling? → Customer pays you directly. Cash/UPI. Ecohomely doesn't handle payments.
- How to get bookings? → Complete profile, stay Online. Customers call you directly.
- Leads guarantee? → No. Active + online = more chances.
- Tools? → Worker brings own.
- Multiple professions? → Yes, "Add Secondary Profession" in app.
- Online/Offline? → Online = visible. Offline = hidden.
- Paid plan forced? → No. Free version continues always.
- Fine if busy? → No fine. Just inform customer.
- Petrol allowance? → No. Discuss travel with customer.
- Referral? → Cash reward when friends join.
- Location? → App → Account → Location → Save.

══════════════════════════════════════
BOOKING FLOW (customers only — ask one at a time in ${langName})
══════════════════════════════════════
1. Full name → 2. Phone number (MANDATORY — do not skip; email is NOT needed) → 3. Service needed → 4. Date & time → 5. Vizag address → 6. Notes
- You MUST collect a valid phone number (10 digits, optionally with country code) before moving to step 3. If the user gives only email or skips, ask again for phone number only. Do not accept booking without phone.
→ Summarize (include phone in summary) → confirm → [BOOKING_COMPLETE: {name, contact, service, datetime, address}]
(contact = phone number only; do not use email)

══════════════════════════════════════
PAYMENT DISCLAIMER (CRITICAL)
══════════════════════════════════════
Always mention: "Ecohomely does not handle payments. All payments are directly between customer and worker via Cash/UPI."
Never claim Ecohomely collects, holds, or manages customer money.

══════════════════════════════════════
STRICT RULES
══════════════════════════════════════
- For bookings: NEVER complete or summarize without a valid phone number from the user. Email is not required; phone is mandatory.
- NEVER say Ecohomely sends workers
- NEVER quote fixed prices
- NEVER promise guaranteed leads
- ALWAYS clarify: Ecohomely doesn't handle payments (direct customer-worker)
- Off-topic → "I can only help with Ecohomely-related questions."
- Outside Vizag → "Serving Visakhapatnam now, expanding to AP & Telangana soon"
- Unsure → [HUMAN_HANDOFF]
- Human request → [HUMAN_HANDOFF]`;



// ─── HELPERS ─────────────────────────────────────────────────────────────────
function genSessionId() {
  return "sess_" + Date.now() + "_" + Math.random().toString(36).slice(2, 8);
}

// Business hours: Mon-Fri 9AM-6PM IST
function isBusinessHours() {
  const now = new Date();
  const ist = new Date(now.toLocaleString("en-US", { timeZone: "Asia/Kolkata" }));
  const day = ist.getDay(); // 0=Sun, 6=Sat
  const hour = ist.getHours();
  return day >= 1 && day <= 5 && hour >= 9 && hour < 18;
}

function getOfflineMsg(langCode) {
  const msgs = {
    en: "Our team is currently offline. We're here Monday to Friday, 9AM–6PM IST. Leave your message and we'll be in touch during these hours. For urgent help call 9491499797.",
    te: "Maa team ippudu offline. Memu Monday nundi Friday 9AM–6PM varaku unnamu. Mee message vadili pettandi, maaku touch lo undagalamu. Urgent ayite 9491499797 ki call cheyyandi.",
    hi: "हमारी टीम अभी offline है। हम सोम–शुक्र 9AM–6PM IST उपलब्ध हैं। अपना message छोड़ें, हम जल्द संपर्क करेंगे। जरूरी हो तो 9491499797 पर कॉल करें।",
  };
  return msgs[langCode] || msgs.en;
}

// Non-Vizag city detection
function detectNonVizagCity(text) {
  const t = text.toLowerCase();
  const nonVizag = ["hyderabad", "bengaluru", "bangalore", "chennai", "mumbai", "delhi", "kolkata", "pune", "vijaywada", "vijayawada", "guntur", "tirupati", "nellore", "kurnool", "rajahmundry", "kakinada"];
  return nonVizag.find(city => t.includes(city)) || null;
}

// Smart service keyword detection
const SERVICE_KEYWORDS = {
  // Two Wheeler Services
  "two wheeler": { label: "Two Wheeler Mechanic", icon: "", desc: "Bike repair, service, maintenance" },
  "bike": { label: "Two Wheeler Mechanic", icon: "", desc: "Bike repair, service, maintenance" },
  "motorcycle": { label: "Two Wheeler Mechanic", icon: "", desc: "Bike repair, service, maintenance" },
  // Four Wheeler Services
  "four wheeler": { label: "Four Wheeler Mechanic", icon: "", desc: "Car repair, service, maintenance" },
  "car": { label: "Four Wheeler Mechanic", icon: "", desc: "Car repair, service, maintenance" },
  "automobile": { label: "Four Wheeler Mechanic", icon: "", desc: "Car repair, service, maintenance" },
  // AC Services
  "ac": { label: "AC Repair", icon: "", desc: "AC repair, service, installation, gas refill" },
  "air condition": { label: "AC Repair", icon: "", desc: "AC repair, service, installation, gas refill" },
  // Appliance Repair
  "appliance": { label: "Appliance Repair", icon: "", desc: "Washing machine, fridge, microwave repair" },
  "refrigerator": { label: "Appliance Repair", icon: "", desc: "Washing machine, fridge, microwave repair" },
  "washing": { label: "Appliance Repair", icon: "", desc: "Washing machine, fridge, microwave repair" },
  // Aquarium Services
  "aquarium": { label: "Aquarium", icon: "", desc: "Aquarium setup, maintenance, cleaning" },
  // Beauty & Wellness
  "beauty": { label: "Beauty & Wellness", icon: "", desc: "Spa, massage, beauty treatments" },
  "wellness": { label: "Beauty & Wellness", icon: "", desc: "Spa, massage, beauty treatments" },
  "massage": { label: "Beauty & Wellness", icon: "", desc: "Spa, massage, beauty treatments" },
  // Car Wash
  "car wash": { label: "Car Wash", icon: "", desc: "Car washing, detailing, cleaning" },
  // Carpenter
  "carpenter": { label: "Carpenter", icon: "", desc: "Furniture, doors, windows, cabinets" },
  "furniture": { label: "Carpenter", icon: "", desc: "Furniture, doors, windows, cabinets" },
  // Catering
  "catering": { label: "Catering Services", icon: "", desc: "Food catering, events, delivery" },
  // Ceiling Work
  "ceiling": { label: "Ceiling", icon: "", desc: "False ceiling, POP, gypsum work" },
  "false ceiling": { label: "Ceiling", icon: "", desc: "False ceiling, POP, gypsum work" },
  // Cleaning
  "clean": { label: "Cleaning", icon: "", desc: "Deep clean, sofa, carpet, bathroom" },
  "sweep": { label: "Cleaning", icon: "", desc: "Deep clean, sofa, carpet, bathroom" },
  // Commercial Vehicle Mechanic
  "commercial": { label: "Commercial Vehicle Mechanic", icon: "", desc: "Bus, truck, commercial vehicle repair" },
  "truck": { label: "Commercial Vehicle Mechanic", icon: "", desc: "Bus, truck, commercial vehicle repair" },
  // Driver Services
  "driver": { label: "Driver", icon: "", desc: "Personal driver, cab driver, outstation" },
  "drivers": { label: "Driver", icon: "", desc: "Personal driver, cab driver, outstation" },
  // Electrician
  "electric": { label: "Electrician", icon: "", desc: "Wiring, fan, switchboard, meter, repairs" },
  "light": { label: "Electrician", icon: "", desc: "Wiring, fan, switchboard, meter, repairs" },
  "fan": { label: "Electrician", icon: "", desc: "Wiring, fan, switchboard, meter, repairs" },
  "wiring": { label: "Electrician", icon: "", desc: "Wiring, fan, switchboard, meter, repairs" },
  // Interior Designers
  "interior": { label: "Interior Designer", icon: "", desc: "Interior design, decoration, consultation" },
  "interior designer": { label: "Interior Designer", icon: "", desc: "Interior design, decoration, consultation" },
  "design": { label: "Interior Designer", icon: "", desc: "Interior design, decoration, consultation" },
  // Laundry
  "laundry": { label: "Laundry", icon: "", desc: "Wash, dry clean, ironing, fabric care" },
  "wash": { label: "Laundry", icon: "", desc: "Wash, dry clean, ironing, fabric care" },
  // Mehndi Artist
  "mehndi": { label: "Mehndi Artist Service", icon: "", desc: "Henna application, bridal mehndi" },
  "henna": { label: "Mehndi Artist Service", icon: "", desc: "Henna application, bridal mehndi" },
  // Men's Salon
  "salon": { label: "Men's Salon", icon: "", desc: "Haircut, shave, grooming, styling" },
  "haircut": { label: "Men's Salon", icon: "", desc: "Haircut, shave, grooming, styling" },
  "barber": { label: "Men's Salon", icon: "", desc: "Haircut, shave, grooming, styling" },
  // Multi-Vehicle Mechanic
  "multi vehicle": { label: "Multi Vehicle Mechanic", icon: "", desc: "All vehicle repair and maintenance" },
  "multi-vehicle": { label: "Multi Vehicle Mechanic", icon: "", desc: "All vehicle repair and maintenance" },
  "mechanic": { label: "Multi Vehicle Mechanic", icon: "", desc: "All vehicle repair and maintenance" },
  "repair": { label: "Multi Vehicle Mechanic", icon: "", desc: "All vehicle repair and maintenance" },
  // Painter
  "paint": { label: "Painter", icon: "", desc: "Home painting, wall putty, waterproofing" },
  "wall": { label: "Painter", icon: "", desc: "Home painting, wall putty, waterproofing" },
  // Pest Control
  "pest": { label: "Pest Control", icon: "", desc: "Termite, mosquito, cockroach control" },
  // Photography
  "photo": { label: "Photography", icon: "", desc: "Professional photography, events, portraits" },
  "photography": { label: "Photography", icon: "", desc: "Professional photography, events, portraits" },
  // Plumber
  "plumb": { label: "Plumber", icon: "", desc: "Tap, pipe, tank, motor, leak repair" },
  "water": { label: "Plumber", icon: "", desc: "Tap, pipe, tank, motor, leak repair" },
  "tap": { label: "Plumber", icon: "", desc: "Tap, pipe, tank, motor, leak repair" },
  // RO Water Purifier Services
  "ro": { label: "RO Water Purifier Service", icon: "", desc: "RO installation, servicing, AMC" },
  "ro water": { label: "RO Water Purifier Service", icon: "", desc: "RO installation, servicing, AMC" },
  "purifier": { label: "RO Water Purifier Service", icon: "", desc: "RO installation, servicing, AMC" },
  // Road Assistance
  "road assistance": { label: "Road Assistance", icon: "", desc: "Roadside help, towing, jump start" },
  // Shifting
  "shifting": { label: "Shifting", icon: "", desc: "House shifting, packing, transportation" },
  "moving": { label: "Shifting", icon: "", desc: "House shifting, packing, transportation" },
  // Smart Home
  "smart home": { label: "Smart Home", icon: "", desc: "Smart devices, automation, installation" },
  // Tutors
  "tutor": { label: "Tutors", icon: "", desc: "Academic coaching, online/offline classes" },
  "tutoring": { label: "Tutors", icon: "", desc: "Academic coaching, online/offline classes" },
  // TV Repair
  "tv": { label: "TV Repair", icon: "", desc: "LED, LCD, smart TV repair & service" },
  "television": { label: "TV Repair", icon: "", desc: "LED, LCD, smart TV repair & service" },
  // TC Repair
  "tc": { label: "TC Repair", icon: "", desc: "TC repair, service, maintenance" },
  "tc repair": { label: "TC Repair", icon: "", desc: "TC repair, service, maintenance" },
  // Water Can Delivery
  "water can": { label: "Water Can Delivery", icon: "", desc: "Drinking water can delivery & supply" },
  "water delivery": { label: "Water Can Delivery", icon: "", desc: "Drinking water can delivery & supply" },
};

function detectServiceSuggestions(text) {
  const t = text.toLowerCase();
  const found = new Map();
  for (const [kw, svc] of Object.entries(SERVICE_KEYWORDS)) {
    if (t.includes(kw) && !found.has(svc.label)) {
      found.set(svc.label, svc);
    }
  }
  return Array.from(found.values()).slice(0, 3);
}

// CSV Export
function exportCSV(sessions) {
  const rows = [["Session ID", "Started", "Language", "User Type", "Messages", "First Message"]];
  for (const s of sessions) {
    const firstMsg = s.messages?.find(m => m.role === "user")?.content?.slice(0, 80) || "";
    rows.push([s.sessionId, s.startTimeStr, s.lang, s.userType || "unknown", s.messages?.length || 0, '"' + firstMsg.replace(/"/g, '""') + '"']);
  }
  const csv = rows.map(r => r.join(",")).join("\n");
  const blob = new Blob([csv], { type: "text/csv" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url; a.download = "ecohomely_chats.csv"; a.click();
  URL.revokeObjectURL(url);
}

// EmailJS alert (free tier) — uses public service
async function sendEmailAlert(enquiry, emailJsKey) {
  if (!emailJsKey) return;
  try {
    await fetch("https://api.emailjs.com/api/v1.0/email/send", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        service_id: "service_ecohomely",
        template_id: "template_enquiry",
        user_id: emailJsKey,
        template_params: {
          to_email: "support@ecohomely.com",
          enquiry_type: enquiry.type,
          preview: enquiry.preview,
          lang: enquiry.lang,
          user_type: enquiry.userType || "unknown",
          time: enquiry.timeStr,
        }
      })
    });
  } catch (e) { console.log("Email alert skipped:", e.message); }
}

function now12() {
  return new Date().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
}

function nowFull() {
  return new Date().toLocaleString("en-IN", {
    day: "2-digit", month: "short", year: "numeric",
    hour: "2-digit", minute: "2-digit",
  });
}

function isEnquiry(text) {
  const t = text.toLowerCase();
  return t.includes("[human_handoff]") || t.includes("[booking_complete") ||
    /\b(book|service|plumber|electrician|cleaning|repair|join|register|price|cost|enqui|complaint|salary|commission|payment)\b/.test(t);
}

function getEnquiryType(text) {
  const t = text.toLowerCase();
  if (t.includes("[booking_complete")) return "Booking Request";
  if (t.includes("[human_handoff]")) return "Human Handoff";
  if (/\b(join|register|worker|commission|earn)\b/.test(t)) return "Worker Enquiry";
  if (/\b(book|service|plumber|electrician|cleaning|repair|price|cost)\b/.test(t)) return "Service Enquiry";
  if (/\b(complaint|problem|issue)\b/.test(t)) return "Complaint";
  return "General Enquiry";
}

function parseBooking(text) {
  // Match [BOOKING_COMPLETE: {...}] or BOOKING_COMPLETE: {...}
  let m = text.match(/\[BOOKING_COMPLETE:\s*(\{[\s\S]*?\})\]/);
  if (!m) m = text.match(/BOOKING_COMPLETE:\s*(\{[\s\S]*?\})/);
  if (!m) return null;
  const raw = m[1].trim();
  try {
    // Try JSON.parse with quoted keys (values may contain commas)
    const jsonStr = raw.replace(/(\w+):/g, '"$1":');
    const parsed = JSON.parse(jsonStr);
    if (parsed && typeof parsed === "object") return parsed;
  } catch (_) {}
  try {
    // Fallback: split by ", key:" to handle values with commas (e.g. address)
    const pairs = raw.replace(/^\{|\}$/g, "").split(/,\s*(?=\w+:\s*)/);
    const data = {};
    for (const p of pairs) {
      const idx = p.indexOf(":");
      if (idx === -1) continue;
      const key = p.slice(0, idx).trim();
      const val = p.slice(idx + 1).trim();
      if (key) data[key] = val;
    }
    return Object.keys(data).length ? data : { info: "Booking captured" };
  } catch {
    return { info: "Booking captured" };
  }
}

// Backend API base for sync-to-sheet (same as rest of admin panel)
const CHAT_SYNC_API_BASE = process.env.REACT_APP_API_URL || "https://us-central1-workexpress-1ffff.cloudfunctions.net/app";

async function syncSessionToGoogleSheet(sessionId, payload) {
  try {
    const body = {
      sessionId,
      startTime: payload.startTime,
      startTimeStr: payload.startTimeStr,
      lastActivity: payload.lastActivity,
      lastActivityStr: payload.lastActivityStr,
      lang: payload.lang,
      userType: payload.userType ?? null,
      sessionEnded: payload.sessionEnded ?? false,
      messages: payload.messages ?? [],
    };
    const res = await fetch(`${CHAT_SYNC_API_BASE}/chat-history/sync-to-sheet`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    });
    if (!res.ok) console.warn("Chat sync to sheet failed:", res.status);
  } catch (e) {
    console.warn("Chat sync to sheet failed:", e.message);
  }
}

// ─── STORAGE UTILS ────────────────────────────────────────────────────────────
async function saveSessionToFirestore(sessionId, sessionData) {
  try {
    if (!db) return;
    const docRef = doc(db, CHATS_HISTORY_COLLECTION, sessionId);
    const payload = {
      sessionId,
      startTime: sessionData.startTime ?? Date.now(),
      startTimeStr: sessionData.startTimeStr ?? new Date().toLocaleString("en-IN", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }),
      lastActivity: sessionData.lastActivity ?? Date.now(),
      lastActivityStr: new Date().toLocaleString("en-IN", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }),
      lang: sessionData.lang ?? "en",
      userType: sessionData.userType ?? null,
      messages: sessionData.messages ?? [],
      sessionEnded: sessionData.sessionEnded ?? false,
      updatedAt: serverTimestamp(),
    };
    if (!payload.startTimeStr && sessionData.startTime) {
      payload.startTimeStr = new Date(sessionData.startTime).toLocaleString("en-IN", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
    }
    await setDoc(docRef, payload, { merge: true });
    await syncSessionToGoogleSheet(sessionId, payload);
  } catch (e) {
    console.warn("Firestore chats_history save failed:", e);
  }
}

async function saveSession(sessionId, sessionData) {
  try {
    if (window.storage) {
      await window.storage.set(`chat:${sessionId}`, JSON.stringify(sessionData));
    }
    await saveSessionToFirestore(sessionId, sessionData);
  } catch (e) { console.warn("Storage save failed:", e); }
}

async function saveEnquiry(enquiryData) {
  try {
    if (window.storage) {
      const key = `enquiry:${Date.now()}`;
      await window.storage.set(key, JSON.stringify(enquiryData));
    }
  } catch (e) { console.warn("Enquiry save failed:", e); }
}

async function loadAllSessions() {
  try {
    if (!window.storage) return [];
    const result = await window.storage.list("chat:");
    const sessions = [];
    for (const key of (result?.keys || [])) {
      try {
        const r = await window.storage.get(key);
        if (r?.value) sessions.push(JSON.parse(r.value));
      } catch (e) { }
    }
    return sessions.sort((a, b) => (b.startTime || 0) - (a.startTime || 0));
  } catch (e) { return []; }
}

async function loadAllEnquiries() {
  try {
    if (!window.storage) return [];
    const result = await window.storage.list("enquiry:");
    const enqs = [];
    for (const key of (result?.keys || [])) {
      try {
        const r = await window.storage.get(key);
        if (r?.value) enqs.push({ key, ...JSON.parse(r.value) });
      } catch (e) { }
    }
    return enqs.sort((a, b) => (b.timestamp || 0) - (a.timestamp || 0));
  } catch (e) { return []; }
}

async function clearAllData() {
  try {
    if (!window.storage) return;
    const r1 = await window.storage.list("chat:");
    const r2 = await window.storage.list("enquiry:");
    for (const key of [...(r1?.keys || []), ...(r2?.keys || [])]) {
      await window.storage.delete(key);
    }
  } catch (e) { }
}

// ─── PARTICLE COMPONENT ──────────────────────────────────────────────────────
function FloatingParticles() {
  const particles = Array.from({ length: 24 }, (_, i) => {
    const types = ["circle", "diamond", "star"];
    return {
      id: i,
      x: Math.random() * 100,
      delay: Math.random() * 10,
      duration: 7 + Math.random() * 10,
      size: 2 + Math.random() * 5,
      opacity: 0.12 + Math.random() * 0.28,
      type: types[i % 3],
      drift: Math.random() > 0.5,
    };
  });
  return (
    <div style={{ position: "absolute", inset: 0, overflow: "hidden", pointerEvents: "none", borderRadius: 24 }}>
      {/* Aurora blob 1 */}
      <div style={{
        position: "absolute", width: 200, height: 200, borderRadius: "50%",
        background: "radial-gradient(circle, rgba(105,240,174,0.12) 0%, transparent 70%)",
        top: "10%", left: "-20%",
        animation: "aurora 12s infinite ease-in-out",
        pointerEvents: "none",
      }} />
      {/* Aurora blob 2 */}
      <div style={{
        position: "absolute", width: 180, height: 180, borderRadius: "50%",
        background: "radial-gradient(circle, rgba(74,158,111,0.10) 0%, transparent 70%)",
        bottom: "15%", right: "-15%",
        animation: "aurora 15s 3s infinite ease-in-out reverse",
        pointerEvents: "none",
      }} />
      {/* Scan line effect */}
      <div style={{
        position: "absolute", left: 0, right: 0, height: 1,
        background: "linear-gradient(90deg,transparent,rgba(105,240,174,0.08),transparent)",
        animation: "scanLine 8s 2s infinite linear",
        pointerEvents: "none",
      }} />
      {particles.map(p => (
        <div key={p.id} style={{
          position: "absolute",
          left: `${p.x}%`,
          bottom: "-10px",
          width: p.size,
          height: p.size,
          borderRadius: p.type === "circle" ? "50%" : p.type === "diamond" ? "2px" : "50%",
          transform: p.type === "diamond" ? "rotate(45deg)" : "none",
          background: p.id % 3 === 0
            ? "linear-gradient(135deg,#69f0ae,#4a9e6f)"
            : p.id % 3 === 1
              ? "rgba(105,240,174,0.6)"
              : "rgba(74,158,111,0.5)",
          opacity: p.opacity,
          animation: `floatUp ${p.duration}s ${p.delay}s infinite linear${p.drift ? `, drift ${3 + p.delay}s ${p.delay * 0.5}s infinite ease-in-out` : ""}`,
          boxShadow: p.id % 4 === 0 ? "0 0 6px rgba(105,240,174,0.4)" : "none",
        }} />
      ))}
    </div>
  );
}

// ─── TYPING DOTS ──────────────────────────────────────────────────────────────
const TypingDots = () => (
  <div style={{ display: "flex", gap: 4, alignItems: "center", padding: "4px 2px" }}>
    {[0, 1, 2].map(i => (
      <span key={i} style={{
        width: 8, height: 8, borderRadius: "50%",
        background: `linear-gradient(135deg,${["#69f0ae", "#4a9e6f", "#2d8a5a"][i]},${["#4a9e6f", "#2d6a4f", "#1b5e35"][i]})`,
        display: "inline-block",
        animation: "bounce 1.1s infinite",
        animationDelay: `${i * 0.18}s`,
        boxShadow: `0 0 8px rgba(105,240,174,${0.3 + i * 0.15})`,
      }} />
    ))}
    <span style={{ fontSize: 10, color: "#81c784", marginLeft: 4, animation: "shimmer 3s infinite", backgroundImage: "linear-gradient(90deg,#4a9e6f,#81c784,#4a9e6f)", backgroundSize: "200%", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent" }}>typing...</span>
  </div>
);

// ─── LINK STYLE ───────────────────────────────────────────────────────────────
const linkStyle = { display: "block", fontSize: 13, color: "#2e7d32", textDecoration: "none", fontWeight: 600, padding: "3px 0" };

// ─── CARDS ────────────────────────────────────────────────────────────────────
const HumanHandoffCard = () => (
  <div style={{ background: "linear-gradient(135deg,#e8f5e9,#f1f8e9)", border: "1.5px solid #81c784", borderRadius: 14, padding: "13px 15px", marginTop: 4 }}>
    <div style={{ fontWeight: 700, color: "#2e7d32", marginBottom: 5, fontSize: 13 }}>Connecting to our team...</div>
    <div style={{ fontSize: 12, color: "#388e3c" }}>Monday–Friday, 9AM–6PM IST. We'll be in touch.</div>
    <div style={{ marginTop: 8, display: "flex", flexDirection: "column", gap: 4 }}>
      <a href="tel:9491499797" style={linkStyle}>Phone: 9491499797</a>
      <a href="mailto:support@ecohomely.com" style={linkStyle}>Email: support@ecohomely.com</a>
      <div style={{ fontSize: 11, color: "#558b2f", marginTop: 3 }}>EcoHomely, Visakhapatnam</div>
    </div>
  </div>
);

const BookingCard = ({ data }) => (
  <div style={{ background: "linear-gradient(135deg,#e8f5e9,#f9fbe7)", border: "1.5px solid #66bb6a", borderRadius: 14, padding: "13px 15px", marginTop: 4 }}>
    <div style={{ fontWeight: 700, color: "#2e7d32", marginBottom: 7, fontSize: 13 }}>Booking Submitted!</div>
    {Object.entries(data).map(([k, v]) => (
      <div key={k} style={{ fontSize: 12, color: "#388e3c", marginBottom: 2 }}>
        <b style={{ textTransform: "capitalize" }}>{k}:</b> {v}
      </div>
    ))}
  </div>
);

// ─── IMAGE WITH FALLBACK ───────────────────────────────────────────────────────
function ImageWithFallback({ src, alt, fallbackSize = "small", style = {}, containerStyle = {} }) {
  const [imgError, setImgError] = useState(false);

  const fallbackStyles = {
    small: { fontSize: 14 },
    medium: { fontSize: 18 },
    large: { fontSize: 24 },
    header: { fontSize: 20 }
  };

  // Extract width and height from style if provided
  const width = style.width || "100%";
  const height = style.height || "100%";

  // Check if src is a valid data URI or URL
  const isValidSrc = src && (
    src.startsWith('data:') ||
    src.startsWith('http://') ||
    src.startsWith('https://') ||
    src.startsWith('/')
  );

  // If invalid src or error, show fallback
  if (imgError || !isValidSrc) {
    return (
      <div style={{
        width: width, height: height,
        background: "linear-gradient(135deg,#2d8a5a,#1b5e35)",
        display: "flex", alignItems: "center", justifyContent: "center",
        color: "#fff", fontWeight: "bold",
        fontSize: fallbackStyles[fallbackSize]?.fontSize || 14,
        ...containerStyle
      }}>
        E
      </div>
    );
  }

  return (
    <img
      src={src}
      onError={(e) => {
        // Silently handle error - don't log to avoid console spam
        setImgError(true);
      }}
      onLoad={() => {
        // Reset error state if image loads successfully
        if (imgError) setImgError(false);
      }}
      style={{ width: "100%", height: "100%", objectFit: "cover", background: "transparent", display: "block", ...style }}
      alt={alt}
    />
  );
}

// ─── MESSAGE BUBBLE ───────────────────────────────────────────────────────────
function Bubble({ msg, idx }) {
  const isBot = msg.role === "bot";
  const hasHandoff = isBot && msg.content.includes("[HUMAN_HANDOFF]");
  const bookData = isBot ? parseBooking(msg.content) : null;
  // Strip booking/handoff markers so raw details never show (bracketed or unbracketed)
  const clean = msg.content
    .replace(/\[HUMAN_HANDOFF\]/g, "")
    .replace(/\[BOOKING_COMPLETE:[\s\S]*?\]/g, "")
    .replace(/BOOKING_COMPLETE:\s*\{[\s\S]*?\}/g, "")
    .trim();
  const rtl = msg.lang === "ur";
  return (
    <div style={{
      display: "flex", justifyContent: isBot ? "flex-start" : "flex-end",
      marginBottom: 10, gap: 7, alignItems: "flex-end",
      animation: `bubbleIn 0.35s cubic-bezier(0.34,1.56,0.64,1) both`,
      animationDelay: `${idx * 0.05}s`,
    }}>
      {isBot && (
        <div style={{
          width: 32, height: 32, borderRadius: "50%", flexShrink: 0,
          overflow: "hidden", border: "2px solid #4a9e6f",
          boxShadow: "0 0 12px rgba(74,158,111,0.4)",
          background: "transparent",
          display: "flex", alignItems: "center", justifyContent: "center",
        }}>
          <ImageWithFallback src={`${process.env.PUBLIC_URL || ''}/chatbot-logo.svg`} alt="Eco" fallbackSize="small" />
        </div>
      )}
      <div style={{ maxWidth: "76%", display: "flex", flexDirection: "column", gap: 3 }}>
        {clean && (
          <div style={{
            background: isBot ? "linear-gradient(135deg,#ffffff,#f7fdf9)" : "linear-gradient(135deg,#2d8a5a,#1b5e35)",
            color: isBot ? "#1a2e1f" : "#fff",
            padding: "10px 13px",
            borderRadius: isBot ? "4px 16px 16px 16px" : "16px 4px 16px 16px",
            fontSize: 14, lineHeight: 1.65,
            boxShadow: isBot
              ? "0 2px 12px rgba(0,0,0,0.06), 0 0 0 1px rgba(74,158,111,0.1)"
              : "0 4px 16px rgba(30,100,60,0.4)",
            whiteSpace: "pre-wrap", direction: rtl ? "rtl" : "ltr", textAlign: rtl ? "right" : "left",
          }}>{clean}</div>
        )}
        {hasHandoff && <HumanHandoffCard />}
        {bookData && <BookingCard data={bookData} />}
        <div style={{ fontSize: 10, color: "#9eb8a3", paddingLeft: isBot ? 4 : 0, textAlign: isBot ? "left" : "right" }}>{msg.time}</div>
      </div>
    </div>
  );
}

// ─── LANGUAGE PICKER ─────────────────────────────────────────────────────────
function LangSheet({ current, onSelect, onClose }) {
  const langGroups = {
    "English": LANGUAGES.filter(l => l.code === "en"),
    "Indian Languages": LANGUAGES.filter(l => ["te", "hi", "ta", "kn", "ml", "mr", "bn", "or"].includes(l.code)),
    "Others": LANGUAGES.filter(l => l.code === "ur"),
  };

  return (
    <div style={{
      position: "absolute",
      bottom: 0,
      left: 0,
      right: 0,
      top: 0,
      background: "rgba(0,0,0,0.55)",
      zIndex: 20,
      display: "flex",
      alignItems: "flex-end",
      justifyContent: "stretch",
      borderRadius: 24,
      pointerEvents: "auto",
    }} onClick={onClose}>
      <div style={{
        background: "#fff",
        width: "100%",
        borderRadius: "24px 24px 0 0",
        padding: "22px 16px 28px",
        maxHeight: "85%",
        height: "85%",
        overflowY: "auto",
        animation: "fadeSlideUp 0.4s cubic-bezier(0.34,1.56,0.64,1)",
        boxShadow: "0 -10px 40px rgba(0,0,0,0.3)",
      }} onClick={e => e.stopPropagation()}>

        {/* Header */}
        <div style={{ marginBottom: 20, position: "relative" }}>
          <div style={{ fontWeight: 900, fontSize: 18, color: "#1a2e1f", marginBottom: 5, display: "flex", alignItems: "center", gap: 8 }}>
            Select Language
          </div>
          <div style={{ fontSize: 13, color: "#666" }}>Choose your preferred language to chat with Eco bot</div>
          <button onClick={onClose} style={{
            position: "absolute",
            top: 0,
            right: 0,
            background: "rgba(0,0,0,0.05)",
            border: "1px solid rgba(0,0,0,0.1)",
            borderRadius: 8,
            color: "#666",
            cursor: "pointer",
            width: 32,
            height: 32,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: 16,
            transition: "all 0.2s",
          }} onMouseEnter={e => {
            e.currentTarget.style.background = "rgba(0,0,0,0.1)";
            e.currentTarget.style.color = "#333";
          }} onMouseLeave={e => {
            e.currentTarget.style.background = "rgba(0,0,0,0.05)";
            e.currentTarget.style.color = "#666";
          }}>✕</button>
        </div>

        {/* Language Groups */}
        {Object.entries(langGroups).filter(([_, langs]) => langs.length > 0).map(([group, langs]) => (
          <div key={group} style={{ marginBottom: 18 }}>
            <div style={{ fontSize: 11, fontWeight: 800, color: "#888", marginBottom: 8, textTransform: "uppercase", letterSpacing: "0.5px" }}>
              {group}
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
              {langs.map(l => (
                <button key={l.code} onClick={() => { onSelect(l); onClose(); }} style={{
                  display: "flex", alignItems: "center", gap: 10, padding: "13px 14px", borderRadius: 14,
                  border: current.code === l.code ? "2.5px solid #2d8a5a" : "1.5px solid #e5e5e5",
                  background: current.code === l.code
                    ? "linear-gradient(135deg,#e8f5e9,#f1f8e9)"
                    : "linear-gradient(135deg,#fafafa,#f5f5f5)",
                  cursor: "pointer", textAlign: "left", transition: "all 0.2s cubic-bezier(0.34,1.56,0.64,1)",
                  position: "relative", overflow: "hidden",
                }} onMouseEnter={e => {
                  if (current.code !== l.code) {
                    e.currentTarget.style.background = "linear-gradient(135deg,#f0f0f0,#e8e8e8)";
                    e.currentTarget.style.borderColor = "rgba(74,158,111,0.4)";
                    e.currentTarget.style.transform = "translateY(-2px)";
                  }
                }} onMouseLeave={e => {
                  if (current.code !== l.code) {
                    e.currentTarget.style.background = "linear-gradient(135deg,#fafafa,#f5f5f5)";
                    e.currentTarget.style.borderColor = "#e5e5e5";
                    e.currentTarget.style.transform = "translateY(0)";
                  }
                }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 15, fontWeight: l.code === "en" ? 600 : 800, color: "#1a2e1f", lineHeight: 1.2 }}>{l.code === "en" ? "ENGLISH" : l.label}</div>
                    {l.code !== "en" && <div style={{ fontSize: 11, color: "#999", marginTop: 2 }}>{l.name}</div>}
                  </div>
                  {current.code === l.code && (
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "center", width: 24, height: 24, background: "linear-gradient(135deg,#4a9e6f,#2d6a4f)", borderRadius: "50%", color: "#fff", fontWeight: 900, fontSize: 14 }}>
                      ✓
                    </div>
                  )}
                </button>
              ))}
            </div>
          </div>
        ))}

      </div>
    </div>
  );
}

// ─── NOTIFICATION TOAST (with WhatsApp share) ────────────────────────────────
function Toast({ toastData, onClose }) {
  useEffect(() => {
    const t = setTimeout(onClose, 6000);
    return () => clearTimeout(t);
  }, []);

  const isObj = toastData && typeof toastData === "object";
  const label = isObj ? toastData.type : toastData;
  const preview = isObj ? toastData.preview : "";
  const WA_NUMBER = "919491499797"; // EcoHomely WhatsApp

  const shareWA = () => {
    const msg = encodeURIComponent(
      `New EcoHomely Enquiry!\n*Type:* ${label}\n*Message:* ${preview}\n*Time:* ${new Date().toLocaleString("en-IN")}`
    );
    window.open(`https://wa.me/${WA_NUMBER}?text=${msg}`, "_blank");
  };

  return (
    <div style={{
      position: "fixed", bottom: 115, right: "calc(410px + 28px + 20px)", zIndex: 99998,
      background: "linear-gradient(135deg,#0d2818 0%,#1b4332 40%,#2d6a4f 100%)",
      color: "#fff", borderRadius: 16, padding: "13px 16px 11px",
      boxShadow: "0 12px 40px rgba(0,0,0,0.4),0 0 0 1px rgba(105,240,174,0.25),0 0 20px rgba(74,158,111,0.3)",
      animation: "toastIn 0.4s cubic-bezier(0.34,1.56,0.64,1) both",
      maxWidth: 295, fontSize: 13, lineHeight: 1.5,
      border: "1.5px solid rgba(105,240,174,0.3)",
      backdropFilter: "blur(10px)", overflow: "hidden",
    }}>
      <div style={{ position: "absolute", inset: 0, backgroundImage: "linear-gradient(105deg,transparent 40%,rgba(105,240,174,0.07) 50%,transparent 60%)", backgroundSize: "200%", animation: "shimmer 3s infinite" }} />
      <div style={{ position: "relative", zIndex: 1 }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 3 }}>
          <div style={{ fontWeight: 800, display: "flex", alignItems: "center", gap: 5, fontSize: 13 }}>
            <span style={{ background: "linear-gradient(90deg,#fff,#69f0ae)", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent" }}>New Enquiry!</span>
          </div>
          <button onClick={onClose} style={{ background: "rgba(255,255,255,0.1)", border: "none", color: "rgba(255,255,255,0.6)", borderRadius: 6, width: 20, height: 20, cursor: "pointer", fontSize: 11, display: "flex", alignItems: "center", justifyContent: "center" }}>✕</button>
        </div>
        <div style={{ fontSize: 11, opacity: 0.8, marginBottom: 2 }}>{label}</div>
        {preview && <div style={{ fontSize: 10, opacity: 0.6, marginBottom: 8, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{preview.slice(0, 60)}...</div>}
        {/* WhatsApp share button */}
        <button onClick={shareWA} style={{
          width: "100%", padding: "6px 10px", borderRadius: 9,
          background: "linear-gradient(135deg,#25d366,#128c7e)",
          border: "none", color: "#fff", cursor: "pointer",
          fontWeight: 800, fontSize: 11, display: "flex", alignItems: "center", justifyContent: "center", gap: 6,
          boxShadow: "0 3px 12px rgba(37,211,102,0.4)",
        }}>
          <svg width="13" height="13" viewBox="0 0 24 24" fill="#fff"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z" /></svg>
          Send via WhatsApp
        </button>
      </div>
    </div>
  );
}

// ─── SERVICE CATEGORY MODAL ──────────────────────────────────────────────────
// ─── SERVICE CATEGORY MODAL ──────────────────────────────────────────────────
function ServiceCategoryModal({ onSelect, onClose, lang }) {
  const [search, setSearch] = useState("");
  const services = Array.from(new Map(
    Object.values(SERVICE_KEYWORDS).map(s => [s.label, s])
  ).values());

  const filtered = search.trim() === ""
    ? services
    : services.filter(s => s.label.toLowerCase().includes(search.toLowerCase()) ||
      s.desc.toLowerCase().includes(search.toLowerCase()));

  const groupedServices = filtered.reduce((acc, s) => {
    const category = s.label.split(" ").slice(1).join(" ");
    if (!acc[category]) acc[category] = [];
    acc[category].push(s);
    return acc;
  }, {});

  return (
    <div style={{
      position: "absolute",
      bottom: 0,
      left: 0,
      right: 0,
      top: 0,
      background: "rgba(0,0,0,0.55)",
      zIndex: 25,
      display: "flex",
      alignItems: "flex-end",
      justifyContent: "stretch",
      borderRadius: 24,
      pointerEvents: "auto",
    }}>
      <div style={{
        background: "#fff",
        borderRadius: "24px 24px 0 0",
        width: "100%",
        maxHeight: "85%",
        height: "85%",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
        boxShadow: "0 -10px 40px rgba(0,0,0,0.3)",
        animation: "fadeSlideUp 0.3s cubic-bezier(0.34,1.56,0.64,1) both",
      }}>
        {/* Header */}
        <div style={{
          background: "linear-gradient(135deg,#2d8a5a,#1b5e35)",
          padding: "18px 16px", display: "flex", alignItems: "center",
          gap: 12, flexShrink: 0, color: "#fff",
        }}>
          <div style={{ flex: 1 }}>
            <div style={{ fontWeight: 900, fontSize: 15 }}>Browse Services</div>
            <div style={{ fontSize: 10, opacity: 0.8, marginTop: 1 }}>{filtered.length} services available</div>
          </div>
          <button onClick={onClose} style={{
            background: "rgba(255,255,255,0.15)", border: "none", borderRadius: 8,
            color: "#fff", cursor: "pointer", width: 28, height: 28,
            display: "flex", alignItems: "center", justifyContent: "center",
            fontSize: 14, fontWeight: 700,
          }}>✕</button>
        </div>

        {/* Search */}
        <div style={{ padding: "12px 14px", background: "#f9fafb", borderBottom: "1px solid #eee" }}>
          <input
            type="text"
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search services..."
            style={{
              width: "100%", padding: "10px 12px", borderRadius: 10,
              border: "1.5px solid #e0e0e0", fontSize: 13,
              outline: "none", boxSizing: "border-box",
            }}
          />
        </div>

        {/* Services List */}
        <div style={{ flex: 1, overflowY: "auto", padding: "12px 14px" }}>
          {Object.entries(groupedServices).map(([category, items]) => (
            <div key={category} style={{ marginBottom: 20 }}>
              <div style={{ fontWeight: 800, fontSize: 13, color: "#1a2e1f", marginBottom: 8, paddingBottom: 6, borderBottom: "2px solid #e8f5e9" }}>
                {category}
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                {items.map((s, idx) => (
                  <button
                    key={idx}
                    onClick={() => { onSelect(s.label); onClose(); }}
                    style={{
                      padding: "12px 14px", borderRadius: 12, border: "1.5px solid #e8f5e9",
                      background: "#fff", cursor: "pointer", textAlign: "left",
                      transition: "all 0.2s", display: "flex", alignItems: "center", gap: 10,
                    }}
                    onMouseEnter={e => {
                      e.currentTarget.style.background = "#f0faf4";
                      e.currentTarget.style.borderColor = "#4a9e6f";
                      e.currentTarget.style.transform = "translateX(4px)";
                    }}
                    onMouseLeave={e => {
                      e.currentTarget.style.background = "#fff";
                      e.currentTarget.style.borderColor = "#e8f5e9";
                      e.currentTarget.style.transform = "translateX(0)";
                    }}
                  >
                    <span style={{ fontSize: 20 }}>{s.icon || ""}</span>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: 700, color: "#1a2e1f", fontSize: 13 }}>{s.label}</div>
                      <div style={{ fontSize: 11, color: "#666", marginTop: 2 }}>{s.desc}</div>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ─── SERVICE SUGGESTION BAR ───────────────────────────────────────────────────
function ServiceSuggestionBar({ suggestions, onSelect }) {
  if (!suggestions || suggestions.length === 0) return null;
  return (
    <div style={{
      padding: "6px 10px 2px", flexShrink: 0, position: "relative", zIndex: 1,
      animation: "fadeSlideIn 0.3s ease both",
    }}>
      <div style={{ fontSize: 9, color: "#81c784", fontWeight: 700, marginBottom: 4, letterSpacing: "0.3px" }}>SUGGESTED SERVICES</div>
      <div style={{ display: "flex", gap: 5, flexWrap: "wrap" }}>
        {suggestions.map((svc, i) => (
          <button key={i} onClick={() => onSelect(`I need ${svc.label.replace(/^[^ ]+ /, "")} service`)}
            style={{
              background: "linear-gradient(135deg,#e8f5e9,#f1f8e9)",
              border: "1.5px solid rgba(74,158,111,0.4)",
              borderRadius: 20, padding: "5px 12px",
              fontSize: 11, color: "#2d6a4f", cursor: "pointer",
              fontWeight: 700, transition: "all 0.2s",
              boxShadow: "0 2px 8px rgba(74,158,111,0.12)",
              display: "flex", alignItems: "center", gap: 4,
            }}>
            <span>{svc.icon}</span>
            <span>{svc.label.replace(/^[^ ]+ /, "")}</span>
          </button>
        ))}
      </div>
    </div>
  );
}

export default function EcoChatbotWidget() {
  const [open, setOpen] = useState(false);
  const [showLang, setShowLang] = useState(false);
  const [showServices, setShowServices] = useState(false);
  const [askingToClose, setAskingToClose] = useState(false);
  const [lang, setLang] = useState(LANGUAGES[0]);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [history, setHistory] = useState([]);
  const [sessionId, setSessionId] = useState(genSessionId);
  const [sessionData, setSessionData] = useState(null);
  const [enquiryCount, setEnquiryCount] = useState(0);
  const [toast, setToast] = useState(null);
  const [toastShown, setToastShown] = useState(false); // Track if toast has been shown for this session
  const [userType, setUserType] = useState(null);
  const [serviceSuggestions, setServiceSuggestions] = useState([]);
  const [offlineShown, setOfflineShown] = useState(false);
  const bottomRef = useRef(null);
  const inputRef = useRef(null);
  const sessionRef = useRef(null);

  // Init on language change
  useEffect(() => {
    const t = now12();
    const welcome = { role: "bot", content: WELCOME[lang.code] || WELCOME.en, time: t, lang: lang.code };
    setMessages([welcome]);
    setHistory([]);
    setUserType(null);
    setToastShown(false); // Reset toast shown state on language change
    const sd = {
      sessionId, lang: lang.code, startTime: Date.now(),
      startTimeStr: nowFull(), userType: null, messages: [welcome],
    };
    setSessionData(sd);
    sessionRef.current = sd;
  }, [lang.code]);

  // Load enquiry count on mount
  useEffect(() => {
    (async () => {
      const enqs = await loadAllEnquiries();
      setEnquiryCount(enqs.length);
    })();
  }, []);

  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages, loading]);
  useEffect(() => { if (open) { setTimeout(() => inputRef.current?.focus(), 100); } }, [open]);

  const resetChatState = () => {
    const newId = genSessionId();
    const t = now12();
    const welcome = { role: "bot", content: WELCOME[lang.code] || WELCOME.en, time: t, lang: lang.code };
    setSessionId(newId);
    setMessages([welcome]);
    setHistory([]);
    setAskingToClose(false);
    setToastShown(false);
    setUserType(null);
    setServiceSuggestions([]);
    setOfflineShown(false);
    setInput("");
    const sd = {
      sessionId: newId,
      lang: lang.code,
      startTime: Date.now(),
      startTimeStr: nowFull(),
      userType: null,
      messages: [welcome],
    };
    setSessionData(sd);
    sessionRef.current = sd;
  };

  const closeAndClearChat = () => {
    setOpen(false);
    resetChatState();
  };

  // Detect user type from message
  function detectUserType(text) {
    const t = text.toLowerCase();
    const workerSignals = ["i am a worker", "i am worker", "nenu worker", "naan worker", "naan electrician", "naan plumber", "i am electrician", "i am plumber", "i am carpenter", "how to join", "how do i join", "join ecohomely", "register as worker", "worker registration", "commission", "earn money", "get customers", "bookings vasthaya", "how to register"];
    const customerSignals = ["need a", "book a", "want a", "book service", "need plumber", "need electrician", "need cleaning", "need repair", "need ac", "need painter", "book cleaning", "hire", "find worker"];
    if (workerSignals.some(s => t.includes(s))) return "worker";
    if (customerSignals.some(s => t.includes(s))) return "customer";
    return null;
  }

  // Detect yes/no response for closing session
  function detectCloseSessionResponse(text) {
    const t = text.toLowerCase().trim();
    const yesSignals = ["yes", "yep", "yeah", "yup", "sure", "ok", "okay", "alright", "fine", "close", "end", "done", "finish", "complete", "that's all", "that's it", "nothing else", "no more", "no thanks", "nahi", "haan", "achha", "theek", "okay", "ok", "close session", "end session"];
    const noSignals = ["no", "nope", "not yet", "wait", "continue", "more", "another", "else", "other", "nahi", "na", "kuch aur", "aur", "continue", "keep going", "not done", "still need"];

    // Check for yes signals
    if (yesSignals.some(s => t === s || t.startsWith(s + " ") || t.endsWith(" " + s) || t.includes(" " + s + " "))) {
      return "yes";
    }
    // Check for no signals
    if (noSignals.some(s => t === s || t.startsWith(s + " ") || t.endsWith(" " + s) || t.includes(" " + s + " "))) {
      return "no";
    }
    return null;
  }

  const send = async (text) => {
    const txt = text || input.trim();
    if (!txt || loading) return;

    // Clear input immediately after getting the text
    setInput("");

    // ── CHECK IF USER IS RESPONDING TO "CLOSE SESSION" PROMPT ──
    if (askingToClose) {
      const closeResponse = detectCloseSessionResponse(txt);

      if (closeResponse === "yes") {
        // User wants to close session
        setAskingToClose(false);

        // Save final session data
        const finalSessionData = {
          ...(sessionRef.current || {}),
          messages: [...(sessionRef.current?.messages || []), { role: "user", content: txt, time: now12(), lang: lang.code }],
          lastActivity: Date.now(),
          sessionEnded: true,
        };
        sessionRef.current = finalSessionData;
        await saveSession(sessionId, finalSessionData);

        // Add confirmation message
        const confirmMsg = {
          role: "bot",
          content: lang.code === "te"
            ? "Thank you! Your session has been saved. We'll be here whenever you need us again!"
            : lang.code === "hi"
              ? "धन्यवाद! आपका session save हो गया है। जब भी आपको जरूरत हो, हम यहाँ होंगे!"
              : "Thank you! Your session has been saved. We'll be here whenever you need us again!",
          time: now12(),
          lang: lang.code
        };
        setMessages(p => [...p, { role: "user", content: txt, time: now12(), lang: lang.code }, confirmMsg]);

        setLoading(false);
        closeAndClearChat();
        return;
      } else if (closeResponse === "no") {
        // User wants to continue - clear the asking state
        setAskingToClose(false);
        // Continue processing the message normally - don't return, let it flow through
      } else {
        // Unclear response, ask again
        const unclearMsg = {
          role: "bot",
          content: lang.code === "te"
            ? "Please let me know: Would you like to close the session, or continue chatting? (Yes to close / No to continue)"
            : lang.code === "hi"
              ? "कृपया बताएं: क्या आप session बंद करना चाहते हैं, या chat जारी रखना चाहते हैं? (बंद करने के लिए Yes / जारी रखने के लिए No)"
              : "Please let me know: Would you like to close the session, or continue chatting? (Yes to close / No to continue)",
          time: now12(),
          lang: lang.code
        };
        setMessages(p => [...p, { role: "user", content: txt, time: now12(), lang: lang.code }, unclearMsg]);
        setLoading(false);
        return;
      }
    }

    // Check if AI is configured via .env (no UI for key)
    if (!hasConfiguredKey()) {
      const userMsg = { role: "user", content: txt, time: now12(), lang: lang.code };
      setMessages(p => [...p, userMsg]);
      const botMsg = { role: "bot", content: CHAT_UNAVAILABLE_MSG, time: now12(), lang: lang.code };
      setMessages(p => [...p, botMsg]);
      return;
    }

    setServiceSuggestions([]); // clear suggestions on new send

    // ── BUSINESS HOURS CHECK ──
    if (!isBusinessHours() && !offlineShown) {
      setOfflineShown(true);
      const offMsg = { role: "bot", content: getOfflineMsg(lang.code), time: now12(), lang: lang.code };
      setMessages(p => [...p, offMsg]);
      // still let the message go through after showing offline notice
    }

    const t = now12();
    const userMsg = { role: "user", content: txt, time: t, lang: lang.code };
    setMessages(p => [...p, userMsg]);
    setLoading(true);

    // ── SMART SERVICE SUGGESTIONS ──
    const svcs = detectServiceSuggestions(txt);
    if (svcs.length > 0) setServiceSuggestions(svcs);

    // ── NON-VIZAG CITY DETECTION ──
    const foreignCity = detectNonVizagCity(txt);

    // ── USER TYPE DETECTION ──
    const detected = detectUserType(txt);
    const newUserType = detected || userType;
    if (detected) setUserType(detected);

    const newHist = [...history, { role: "user", content: txt }];

    // Update session
    const updatedSession = {
      ...(sessionRef.current || {}),
      userType: newUserType,
      messages: [...(sessionRef.current?.messages || []), userMsg],
    };
    sessionRef.current = updatedSession;

    try {
      // Get API key based on provider
      let currentApiKey = "";
      let provider = API_PROVIDER;

      if (provider === "openai") {
        currentApiKey = (OPENAI_API_KEY || process.env.REACT_APP_OPENAI_API_KEY || "").trim();
        if (currentApiKey && !currentApiKey.startsWith('sk-')) {
          const errMsg = { role: "bot", content: CHAT_UNAVAILABLE_MSG, time: now12(), lang: lang.code };
          setMessages(p => [...p, errMsg]);
          setLoading(false);
          return;
        }
      } else if (provider === "groq") {
        currentApiKey = (GROQ_API_KEY || process.env.REACT_APP_GROQ_API_KEY || "").trim();
        if (currentApiKey && !currentApiKey.startsWith('gsk_')) {
          const errMsg = { role: "bot", content: CHAT_UNAVAILABLE_MSG, time: now12(), lang: lang.code };
          setMessages(p => [...p, errMsg]);
          setLoading(false);
          return;
        }
      } else if (provider === "gemini") {
        currentApiKey = (GEMINI_API_KEY || process.env.REACT_APP_GEMINI_API_KEY || "").trim();
      }

      // Remove any quotes or whitespace
      currentApiKey = currentApiKey.replace(/^["']|["']$/g, '').trim();

      if (!currentApiKey) {
        const errMsg = { role: "bot", content: CHAT_UNAVAILABLE_MSG, time: now12(), lang: lang.code };
        setMessages(p => [...p, errMsg]);
        setLoading(false);
        return;
      }

      let res, d;

      // Call appropriate API
      if (provider === "openai") {
        res = await fetch("https://api.openai.com/v1/chat/completions", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "Authorization": `Bearer ${currentApiKey}`
          },
          body: JSON.stringify({
            model: "gpt-3.5-turbo", // You can change to "gpt-4" if you have access
            max_tokens: 1000,
            messages: [{ role: "system", content: buildPrompt(lang.code, lang.name) }, ...newHist],
          }),
        });
        d = await res.json();
      } else if (provider === "groq") {
        res = await fetch("https://api.groq.com/openai/v1/chat/completions", {
          method: "POST",
          headers: { "Content-Type": "application/json", "Authorization": `Bearer ${currentApiKey}` },
          body: JSON.stringify({
            model: "llama-3.3-70b-versatile",
            max_tokens: 1000,
            messages: [{ role: "system", content: buildPrompt(lang.code, lang.name) }, ...newHist],
          }),
        });
        d = await res.json();
      } else if (provider === "gemini") {
        // Convert messages to Gemini format
        const geminiMessages = newHist.map(msg => ({
          role: msg.role === "user" ? "user" : "model",
          parts: [{ text: msg.content }]
        }));

        // Add system instruction as the first user message
        const systemInstruction = buildPrompt(lang.code, lang.name);
        geminiMessages.unshift({
          role: "user",
          parts: [{ text: `System Instruction: ${systemInstruction}` }]
        });
        geminiMessages.push({
          role: "model",
          parts: [{ text: "Understood. I'll follow these instructions." }]
        });

        res = await fetch(`https://generativelanguage.googleapis.com/v1beta/models/gemini-pro:generateContent?key=${currentApiKey}`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            contents: geminiMessages,
            generationConfig: {
              maxOutputTokens: 1000,
              temperature: 0.7,
            }
          }),
        });
        d = await res.json();

        // Transform Gemini response to match expected format
        if (res.ok && d.candidates && d.candidates[0]?.content?.parts) {
          d = {
            choices: [{
              message: {
                content: d.candidates[0].content.parts[0].text
              }
            }]
          };
        }
      }

      // Better error handling
      if (!res.ok) {
        console.error("API Error:", d);
        let reply = "API Error: ";
        if (d.error?.message) {
          reply += d.error.message;
        } else if (d.error?.type === "invalid_request_error" || res.status === 401) {
          reply = CHAT_UNAVAILABLE_MSG;
        } else if (res.status === 429) {
          reply = "Rate limited. Please wait a moment and try again.";
        } else {
          reply += JSON.stringify(d.error) || "Unknown error";
        }
        const botMsg = { role: "bot", content: reply, time: now12(), lang: lang.code };
        setMessages(p => [...p, botMsg]);
        setLoading(false);
        return;
      }

      let reply = d.choices?.[0]?.message?.content || "Sorry, please try again.";

      // ── 📍 INJECT CITY NOTE if outside Vizag ──
      if (foreignCity) {
        reply = `We're currently serving Visakhapatnam only — ${foreignCity.charAt(0).toUpperCase() + foreignCity.slice(1)} isn't live yet, but we're expanding to AP & Telangana soon! Stay tuned\n\n` + reply;
      }

      const bt = now12();
      const botMsg = { role: "bot", content: reply, time: bt, lang: lang.code };
      setMessages(p => [...p, botMsg]);

      const finalHistory = [...newHist, { role: "assistant", content: reply }];
      setHistory(finalHistory);

      // Update & save session
      const finalSession = {
        ...updatedSession,
        messages: [...updatedSession.messages, botMsg],
        lastActivity: Date.now(),
      };
      sessionRef.current = finalSession;
      await saveSession(sessionId, finalSession);

      // ── ENQUIRY DETECTION + EMAIL ALERT ──
      // Save all enquiries, but only show toast once per session and not during active booking flow
      if (isEnquiry(reply) || isEnquiry(txt)) {
        const enquiryType = getEnquiryType(txt + " " + reply);
        const replyLower = reply.toLowerCase();
        const isBookingFlow = replyLower.includes("name") ||
          replyLower.includes("phone") ||
          replyLower.includes("email") ||
          replyLower.includes("contact") ||
          replyLower.includes("date") ||
          replyLower.includes("time") ||
          replyLower.includes("address") ||
          replyLower.includes("location") ||
          replyLower.includes("when") ||
          replyLower.includes("where") ||
          replyLower.includes("provide") ||
          replyLower.includes("tell me");

        const enquiry = {
          sessionId,
          type: enquiryType,
          preview: txt.slice(0, 120),
          timestamp: Date.now(),
          timeStr: nowFull(),
          lang: lang.name,
          userType: newUserType,
        };
        await saveEnquiry(enquiry);
        const newCount = enquiryCount + 1;
        setEnquiryCount(newCount);

        // Only show toast once per session and not during active booking flow
        if (!toastShown && !isBookingFlow) {
          setToast({ type: enquiry.type, preview: enquiry.preview, enquiry });
          setToastShown(true);
        }

        // Email alert if key saved
        try {
          const ejKey = typeof localStorage !== "undefined" ? localStorage.getItem("emailjs_key") : null;
          if (ejKey) sendEmailAlert(enquiry, ejKey);
        } catch (e) { }
      }

      // ── CHECK IF WE SHOULD ASK ABOUT CLOSING SESSION ──
      // DO NOT ask during active booking flow (when bot is asking for name, phone, email, date, address)
      const replyLower = reply.toLowerCase();
      const isBookingFlowActive =
        replyLower.includes("name") ||
        replyLower.includes("phone") ||
        replyLower.includes("email") ||
        replyLower.includes("contact") ||
        replyLower.includes("date") ||
        replyLower.includes("time") ||
        replyLower.includes("address") ||
        replyLower.includes("location") ||
        replyLower.includes("when") ||
        replyLower.includes("where") ||
        replyLower.includes("provide") ||
        replyLower.includes("tell me");

      const hasBookingComplete = reply.includes("[BOOKING_COMPLETE") || replyLower.includes("booking is complete") || replyLower.includes("booking complete");

      // Only ask after booking is actually complete, or after 10+ messages (not during booking flow)
      const shouldAskToClose = !askingToClose && !isBookingFlowActive && (
        hasBookingComplete ||
        (finalSession.messages.length >= 10 && !isBookingFlowActive)
      );

      if (shouldAskToClose) {
        setAskingToClose(true);
        const closePrompt = {
          role: "bot",
          content: lang.code === "te"
            ? "Should we help you with anything else, or would you like to close the session?"
            : lang.code === "hi"
              ? "क्या हम आपकी और किसी चीज़ में मदद करें, या क्या आप session बंद करना चाहेंगे?"
              : "Should we help you with anything else, or would you like to close the session?",
          time: now12(),
          lang: lang.code
        };
        setMessages(p => [...p, closePrompt]);
        const updatedSessionWithPrompt = {
          ...finalSession,
          messages: [...finalSession.messages, closePrompt],
        };
        sessionRef.current = updatedSessionWithPrompt;
        await saveSession(sessionId, updatedSessionWithPrompt);
        setLoading(false);
        return;
      }

    } catch (e) {
      const errMsg = { role: "bot", content: "Connection error. Please call us: 9491499797", time: now12(), lang: "en" };
      setMessages(p => [...p, errMsg]);
    }
    setLoading(false);
  };

  const qr = QUICK_REPLIES[lang.code] || QUICK_REPLIES.en;

  return (
    <div className="ecochat-widget">
      {/* ── Toast Notification ── */}
      {toast && <Toast toastData={toast} onClose={() => setToast(null)} />}

      {/* ── Floating Button ── */}
      {!open && (
        <div style={{ position: "fixed", bottom: 28, right: 28, left: "auto", zIndex: 9999 }}>
          {/* Triple ripple rings */}
          <div style={{ position: "absolute", inset: -2, borderRadius: "50%", border: "2px solid rgba(74,158,111,0.4)", animation: "ripple 2.4s 0s infinite" }} />
          <div style={{ position: "absolute", inset: -2, borderRadius: "50%", border: "1.5px solid rgba(105,240,174,0.3)", animation: "ripple2 2.4s 0.8s infinite" }} />
          <div style={{ position: "absolute", inset: 0, borderRadius: "50%", background: "rgba(74,158,111,0.18)", animation: "ripple 2.4s 1.6s infinite" }} />
          {/* Enquiry badge */}
          {enquiryCount > 0 && (
            <div style={{
              position: "absolute", top: -5, right: -5, minWidth: 22, height: 22, borderRadius: 11,
              background: "linear-gradient(135deg,#ff5252,#d32f2f)", color: "#fff", fontSize: 10, fontWeight: 900,
              display: "flex", alignItems: "center", justifyContent: "center",
              border: "2.5px solid white", zIndex: 4, padding: "0 5px",
              boxShadow: "0 2px 10px rgba(255,82,82,0.5)",
              animation: "glowPulse 1.5s infinite",
            }}>
              {enquiryCount}
            </div>
          )}
          <button className="chatbtn" onClick={() => setOpen(!open)} style={{
            width: 64, height: 64, borderRadius: "50%",
            background: "linear-gradient(135deg,#2d8a5a 0%,#1b5e35 50%,#2d8a5a 100%)",
            backgroundSize: "200% 200%",
            border: "2.5px solid rgba(105,240,174,0.35)",
            cursor: "pointer", overflow: "hidden",
            position: "relative", zIndex: 2,
            animation: "pulse 2.5s infinite, neonRing 3s 1s infinite",
            transition: "all 0.3s cubic-bezier(0.34,1.56,0.64,1)",
            boxShadow: "0 8px 30px rgba(45,138,90,0.6)",
          }}>
            <div style={{ width: "100%", height: "100%", display: "flex", alignItems: "center", justifyContent: "center" }}>
              <ImageWithFallback
                src={`${process.env.PUBLIC_URL || ''}/chatbot-logo.svg`}
                alt="ECO"
                fallbackSize="large"
                style={{ width: "110%", height: "110%", animation: "logoGlow 3s infinite" }}
                containerStyle={{ borderRadius: "50%" }}
              />
            </div>
          </button>
          {/* Label below button */}
          <div style={{ position: "absolute", bottom: -22, left: "50%", transform: "translateX(-50%)", whiteSpace: "nowrap", fontSize: 9, fontWeight: 800, color: "#2d6a4f", letterSpacing: "0.5px", textTransform: "uppercase", opacity: 0.8 }}>
            Ecohomely Bot
          </div>
        </div>
      )}

      {/* ── Chat Window ── */}
      {open && (
        <div style={{
          position: "fixed",
          bottom: 28,
          right: 28,
          left: "auto",
          width: "min(410px, calc(100vw - 56px))",
          maxWidth: 410,
          height: "min(640px, calc(100vh - 56px))",
          maxHeight: 640,
          minWidth: 280,
          minHeight: 400,
          background: "linear-gradient(170deg, #f4fbf7 0%, #edf7f0 30%, #e8f5e9 60%, #f0fbf5 100%)",
          borderRadius: 24,
          boxShadow: "0 24px 70px rgba(0,0,0,0.2), 0 4px 20px rgba(74,158,111,0.2), 0 0 0 1px rgba(74,158,111,0.12)",
          display: "flex", flexDirection: "column", overflow: "hidden",
          animation: "fadeSlideUp 0.35s cubic-bezier(0.34,1.56,0.64,1) both",
          zIndex: 9998,
        }}>
          <FloatingParticles />

          {/* ── HEADER (sticky; close button always visible, never clipped) ── */}
          <div style={{
            background: "linear-gradient(135deg,#0d2818 0%,#1b4332 25%,#2d6a4f 60%,#1a5c3a 80%,#0a1f12 100%)",
            backgroundSize: "300% 300%",
            padding: "13px 14px",
            display: "flex", alignItems: "center", gap: 10, flexShrink: 0,
            position: "sticky", top: 0, zIndex: 10,
            boxShadow: "0 4px 24px rgba(0,0,0,0.35), 0 1px 0 rgba(105,240,174,0.15)",
            animation: "headerFlow 8s infinite ease-in-out",
            overflow: "visible",
            minHeight: 52,
          }}>
            {/* Header shimmer sweep */}
            <div style={{
              position: "absolute", inset: 0, pointerEvents: "none",
              background: "linear-gradient(105deg,transparent 40%,rgba(105,240,174,0.06) 50%,transparent 60%)",
              backgroundSize: "200% 100%",
              animation: "shimmer 4s infinite linear",
            }} />
            {/* Header grid lines */}
            <div style={{
              position: "absolute", inset: 0, pointerEvents: "none",
              backgroundImage: "linear-gradient(rgba(105,240,174,0.04) 1px,transparent 1px),linear-gradient(90deg,rgba(105,240,174,0.04) 1px,transparent 1px)",
              backgroundSize: "20px 20px",
            }} />

            {/* Logo + title (can shrink so right controls stay visible) */}
            <div style={{ display: "flex", alignItems: "center", gap: 10, flex: 1, minWidth: 0, position: "relative", zIndex: 2 }}>
              <div style={{ position: "relative", flexShrink: 0 }}>
                {/* Outer glow ring */}
                <div style={{ position: "absolute", inset: -4, borderRadius: "50%", border: "1.5px solid rgba(105,240,174,0.3)", animation: "neonRing 2.5s infinite" }} />
                <ImageWithFallback
                  src={`${process.env.PUBLIC_URL || ''}/chatbot-logo.svg`}
                  alt="ECO"
                  fallbackSize="header"
                  style={{
                    width: 44, height: 44,
                    borderRadius: "50%",
                    border: "2.5px solid rgba(105,240,174,0.6)",
                    boxShadow: "0 0 20px rgba(105,240,174,0.5), inset 0 0 10px rgba(0,0,0,0.3)",
                    animation: "logoGlow 3s infinite",
                    position: "relative", zIndex: 1,
                  }}
                  containerStyle={{
                    borderRadius: "50%",
                    border: "2.5px solid rgba(105,240,174,0.6)",
                    boxShadow: "0 0 20px rgba(105,240,174,0.5)",
                    position: "relative", zIndex: 1,
                  }}
                />
                {/* Status dot with ping */}
                <div style={{ position: "absolute", bottom: 1, right: 1, width: 11, height: 11, zIndex: 2 }}>
                  <div style={{ position: "absolute", inset: 0, borderRadius: "50%", background: "#69f0ae", animation: "statusPing 2s infinite" }} />
                  <div style={{ position: "absolute", inset: 1, borderRadius: "50%", background: "#00e676", border: "1.5px solid #1b4332" }} />
                </div>
              </div>
              <div style={{ position: "relative", zIndex: 2 }}>
                <div style={{
                  fontWeight: 900, fontSize: 15, letterSpacing: "0.2px", lineHeight: 1.2,
                  background: "linear-gradient(135deg,#ffffff 0%,#b9f6ca 50%,#69f0ae 100%)",
                  backgroundSize: "200%",
                  WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent",
                  animation: "shimmer 5s infinite linear",
                }}>Ecohomely Bot</div>
              </div>
            </div>

            {/* Right-side controls: never shrink so Close is always visible */}
            <div style={{ display: "flex", alignItems: "center", gap: 8, flexShrink: 0, position: "relative", zIndex: 3 }}>
              <a href="https://wa.me/919491499797" target="_blank" rel="noreferrer" className="lbtn" title="Chat on WhatsApp" style={{
                background: "rgba(37,211,102,0.2)", border: "1.5px solid rgba(37,211,102,0.4)",
                borderRadius: 9, color: "#fff", cursor: "pointer",
                padding: "5px 8px", fontSize: 11, fontWeight: 700,
                display: "flex", alignItems: "center", gap: 4, transition: "all 0.2s",
                textDecoration: "none", flexShrink: 0,
              }}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="#25d366"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z" /></svg>
              </a>
              <button className="lbtn" onClick={() => setShowServices(true)} title="Browse Services" style={{
                background: "rgba(74,158,111,0.25)", border: "1.5px solid rgba(105,240,174,0.4)",
                borderRadius: 9, color: "#fff", cursor: "pointer",
                padding: "5px 8px", fontSize: 11, fontWeight: 700,
                display: "flex", alignItems: "center", gap: 4, transition: "all 0.2s",
                flexShrink: 0,
              }}>
                <span style={{ fontSize: 11 }}>Services</span>
              </button>
              <button className="lbtn" title="Change Language" onClick={() => setShowLang(true)} style={{
                background: "rgba(255,255,255,0.1)", border: "1.5px solid rgba(255,255,255,0.2)",
                borderRadius: 9, color: "#fff", cursor: "pointer",
                padding: "5px 10px", fontSize: 11, fontWeight: 700,
                display: "flex", alignItems: "center", gap: 4, transition: "all 0.2s",
                flexShrink: 0,
              }}>
                <span style={{ fontSize: 11 }}>{lang.code === "en" ? "ENGLISH" : lang.label}</span>
                <span style={{ fontSize: 9, opacity: 0.6 }}>v</span>
              </button>
              {/* Close button: always present (session open or closed) */}
              <button
                type="button"
                onClick={closeAndClearChat}
                title="Close chat"
                aria-label="Close chat"
                style={{
                  background: "rgba(255,255,255,0.25)",
                  border: "1.5px solid rgba(255,255,255,0.5)",
                  borderRadius: 8,
                  color: "#fff",
                  cursor: "pointer",
                  width: 32,
                  height: 32,
                  minWidth: 32,
                  minHeight: 32,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: 18,
                  fontWeight: 700,
                  lineHeight: 1,
                  transition: "all 0.2s",
                  flexShrink: 0,
                }}
              >
                ×
              </button>
            </div>
          </div>

          {/* Tagline strip */}
          <div style={{ backgroundImage: "linear-gradient(90deg,rgba(27,67,50,0.95),rgba(45,106,79,0.9),rgba(27,67,50,0.95))", backgroundSize: "200%", animation: "headerFlow 6s infinite", padding: "4px 14px", fontSize: 10, color: "#69f0ae", fontWeight: 800, display: "flex", alignItems: "center", gap: 5, flexShrink: 0, borderBottom: "1px solid rgba(105,240,174,0.15)", letterSpacing: "0.3px" }}>
            <div style={{ overflow: "hidden", flex: 1 }}>
              <span style={{ animation: "shimmer 4s infinite linear", backgroundImage: "linear-gradient(90deg,#69f0ae,#b9f6ca,#4a9e6f,#69f0ae)", backgroundSize: "300%", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent" }}>
                0% Commission · 1,200+ Workers · 2,500+ Customers · Visakhapatnam
              </span>
            </div>
          </div>

          {/* ── MESSAGES ── */}
          <div style={{ flex: 1, overflowY: "auto", padding: "13px 13px 8px", display: "flex", flexDirection: "column", position: "relative", zIndex: 1 }}>
            {messages.map((m, i) => <Bubble key={i} msg={m} idx={i} />)}
            {loading && (
              <div style={{ display: "flex", gap: 8, alignItems: "flex-end", marginBottom: 10, animation: "bubbleIn 0.3s ease both" }}>
                <div style={{ width: 32, height: 32, borderRadius: "50%", overflow: "hidden", border: "2px solid #4a9e6f", boxShadow: "0 0 10px rgba(74,158,111,0.3)", flexShrink: 0, background: "transparent", display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <ImageWithFallback src={`${process.env.PUBLIC_URL || ''}/chatbot-logo.svg`} alt="Eco" fallbackSize="small" />
                </div>
                <div style={{ background: "linear-gradient(135deg,#ffffff,#f7fdf9)", borderRadius: "4px 16px 16px 16px", padding: "8px 14px", boxShadow: "0 2px 8px rgba(0,0,0,0.06)" }}>
                  <TypingDots />
                </div>
              </div>
            )}
            <div ref={bottomRef} />
          </div>

          {/* Language Modal - positioned at bottom, above input */}
          {showLang && <LangSheet current={lang} onSelect={(l) => setLang(l)} onClose={() => setShowLang(false)} />}

          {/* Service Modal - positioned at bottom, above input */}
          {showServices && <ServiceCategoryModal onSelect={(txt) => send(txt)} onClose={() => setShowServices(false)} lang={lang} />}

          {/* SMART SERVICE SUGGESTIONS */}
          <ServiceSuggestionBar suggestions={serviceSuggestions} onSelect={(txt) => send(txt)} />

          {/* ── QUICK REPLIES ── */}
          {messages.length <= 1 && serviceSuggestions.length === 0 && (
            <div className="quick-replies-container" style={{
              padding: "3px 10px 6px",
              display: "flex",
              flexWrap: "nowrap",
              gap: 5,
              flexShrink: 0,
              position: "relative",
              zIndex: 1,
              overflowX: "auto",
              overflowY: "hidden",
              scrollbarWidth: "thin",
              WebkitOverflowScrolling: "touch"
            }}>
              {qr.map(q => (
                <button key={q.label} className="qbtn" onClick={() => send(q.text)} style={{
                  background: "rgba(255,255,255,0.9)", backdropFilter: "blur(4px)",
                  border: "1.5px solid rgba(74,158,111,0.4)",
                  borderRadius: 20, padding: "5px 11px",
                  fontSize: 11, color: "#2d6a4f", cursor: "pointer",
                  fontWeight: 700, transition: "all 0.2s",
                  boxShadow: "0 2px 8px rgba(74,158,111,0.1)",
                  whiteSpace: "nowrap",
                  flexShrink: 0,
                }}>{q.label}</button>
              ))}
            </div>
          )}

          {/* ── INPUT ── */}
          <div style={{ padding: "7px 12px 11px", background: "rgba(255,255,255,0.92)", backdropFilter: "blur(10px)", borderTop: "1px solid rgba(74,158,111,0.15)", flexShrink: 0, position: "relative", zIndex: 1 }}>
            {/* Assistance / payment support */}
            <div style={{ marginBottom: 6, padding: "5px 10px", background: "linear-gradient(90deg,#e8f5e9,#f1f8e9)", borderRadius: 8, border: "1px solid rgba(74,158,111,0.25)", fontSize: 10, color: "#2e7d32", fontWeight: 600, lineHeight: 1.4 }}>
              For any assistance or payment-related issues please reach out — Toll-free <a href="tel:9491499797" style={{ color: "#1b5e20", textDecoration: "none", fontWeight: 700 }}>9491499797</a> or <a href="mailto:support@ecohomely.com" style={{ color: "#1b5e20", textDecoration: "none", fontWeight: 700 }}>support@ecohomely.com</a>
            </div>
            {/* Business hours badge */}
            {!isBusinessHours() && (
              <div style={{ display: "flex", alignItems: "center", gap: 5, marginBottom: 6, padding: "4px 10px", background: "linear-gradient(90deg,#fff8e1,#fffde7)", borderRadius: 8, border: "1px solid #ffe082" }}>
                <span style={{ fontSize: 10, color: "#f57f17", fontWeight: 700 }}>Offline — Mon–Fri 9AM–6PM IST · We'll be in touch</span>
              </div>
            )}
            <div style={{ display: "flex", alignItems: "flex-end", gap: 7, background: "#f4fbf6", border: "1.5px solid rgba(74,158,111,0.3)", borderRadius: 16, padding: "7px 7px 7px 12px", boxShadow: "0 2px 12px rgba(74,158,111,0.08)", transition: "box-shadow 0.2s" }}>
              <textarea ref={inputRef} rows={1} value={input}
                onChange={e => { setInput(e.target.value); e.target.style.height = "auto"; e.target.style.height = Math.min(e.target.scrollHeight, 80) + "px"; }}
                onKeyDown={e => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
                placeholder={PLACEHOLDERS[lang.code] || PLACEHOLDERS.en}
                dir={lang.code === "ur" ? "rtl" : "ltr"}
                style={{ flex: 1, border: "none", background: "transparent", resize: "none", fontSize: 14, lineHeight: 1.4, color: "#1a2e1f", maxHeight: 80, overflow: "auto", direction: lang.code === "ur" ? "rtl" : "ltr" }}
              />
              <button onClick={() => send()} disabled={!input.trim() || loading} style={{
                width: 40, height: 40, borderRadius: 13,
                backgroundImage: input.trim() && !loading
                  ? "linear-gradient(135deg,#2d8a5a 0%,#1b5e35 50%,#0a3d1f 100%)"
                  : "linear-gradient(135deg,#e0e0e0,#d0d0d0)",
                backgroundSize: "200%",
                backgroundPosition: "center",
                backgroundRepeat: "no-repeat",
                border: input.trim() && !loading ? "1.5px solid rgba(105,240,174,0.4)" : "1.5px solid transparent",
                cursor: input.trim() && !loading ? "pointer" : "not-allowed",
                display: "flex", alignItems: "center", justifyContent: "center",
                fontSize: 16, flexShrink: 0, transition: "all 0.25s cubic-bezier(0.34,1.56,0.64,1)",
                boxShadow: input.trim() && !loading ? "0 4px 18px rgba(45,138,90,0.55)" : "none",
                animation: input.trim() && !loading ? "neonRing 2s infinite" : "none",
                transform: input.trim() && !loading ? "scale(1)" : "scale(0.95)",
              }}>
                <span style={{ color: input.trim() && !loading ? "#fff" : "#aaa", fontSize: 17 }}>&gt;</span>
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
